import React, { useMemo, useRef, useState, useCallback, useEffect } from "react";
import styled from "styled-components";
import { useSelector, shallowEqual } from "react-redux";
import { computeHierarchyScores, ROOT_ID as ROOT_NODE_ID } from "./scoreUtils";

const OVERALL_METRIC_ID = "overall";
const POSITIVE_EDGE_COLOR = "#27CDA7";
const POSITIVE_EDGE_COLOR_FADE = "#BDE6D9";
const NEGATIVE_EDGE_COLOR = "#F15062";
const NEGATIVE_EDGE_COLOR_FADE = "#F8C4CB";
const DEFAULT_EDGE_COLOR = "#D8E1F0";
const DEFAULT_EDGE_ACTIVE_COLOR = "#AFC3E6";

const NODE_BORDER_COLORS = {
  positive: POSITIVE_EDGE_COLOR,
  negative: NEGATIVE_EDGE_COLOR,
  neutral: DEFAULT_EDGE_ACTIVE_COLOR,
};

const NODE_HIGHLIGHT_SHADOWS = {
  positive: "0 12px 28px rgba(39, 205, 167, 0.25)",
  negative: "0 12px 28px rgba(241, 80, 98, 0.25)",
  neutral: "0 12px 28px rgba(45, 112, 226, 0.25)",
};

const formatScore = (value) => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "--";
  }
  return `${Math.round(value * 10)}%`;
};

const getComparisonStatus = (value, baseline, isBaseline) => {
  if (isBaseline) {
    return "neutral";
  }

  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "neutral";
  }

  if (typeof baseline !== "number" || !Number.isFinite(baseline)) {
    return "neutral";
  }

  if (Math.abs(value - baseline) < 0.001) {
    return "neutral";
  }

  return value >= baseline ? "positive" : "negative";
};

const Hierarchy = ({ coreData: coreDataProp, categories: categoriesProp, organizationName: organizationNameProp, data }) => {
  const storeCoreData = useSelector(
    (state) => state.audit?.core_data,
    shallowEqual
  );

  const coreData = coreDataProp || storeCoreData;
  const organizationName = organizationNameProp
    || coreData?.organization?.name
    || coreData?.organization_name
    || coreData?.organizationName
    || "Organization";
  const categories = categoriesProp || coreData?.categories?.[0]?.categories || [];
  const [scoreContext, setScoreContext] = useState(() => ({
    metricType: "overall",
    metricId: OVERALL_METRIC_ID,
    label: "Culture Average",
  }));
  const { nodeScores, metrics } = useMemo(
    () => computeHierarchyScores(coreData, categories, data),
    [coreData, categories, data]
  );

  const scoreOptions = useMemo(() => {
    const options = [
      {
        value: `${"overall"}|${OVERALL_METRIC_ID}`,
        label: "Culture Average",
        metricType: "overall",
        metricId: OVERALL_METRIC_ID,
      },
    ];

    metrics.factors.forEach((factor) => {
      options.push({
        value: `factor|${factor.id}`,
        label: factor.label,
        metricType: "factor",
        metricId: factor.id,
      });
    });

    metrics.outcomes.forEach((outcome) => {
      options.push({
        value: `outcome|${outcome.id}`,
        label: outcome.label,
        metricType: "outcome",
        metricId: outcome.id,
      });
    });

    return options;
  }, [metrics.factors, metrics.outcomes]);

  const currentSelectValue = useMemo(() => {
    return `${scoreContext.metricType}|${scoreContext.metricId}`;
  }, [scoreContext.metricId, scoreContext.metricType]);
  const fullGraph = useMemo(
    () => buildGraphLayout(categories, organizationName),
    [categories, organizationName]
  );
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [isPanning, setIsPanning] = useState(false);
  const [viewportOffset, setViewportOffset] = useState({ x: 0, y: 0 });
  const panOrigin = useRef(null);
  const viewportRef = useRef(null);
  const [zoom, setZoom] = useState(1);

  const collectBranch = useCallback(
    (startId) => {
      if (!startId) {
        return new Set();
      }

      const ancestors = new Set([startId]);
      const ancestorQueue = [startId];

      while (ancestorQueue.length > 0) {
        const current = ancestorQueue.shift();
        const parents = fullGraph.incoming.get(current) || [];
        parents.forEach((parent) => {
          if (!ancestors.has(parent)) {
            ancestors.add(parent);
            ancestorQueue.push(parent);
          }
        });
      }

      const branch = new Set(ancestors);
      const descendantQueue = [startId];

      while (descendantQueue.length > 0) {
        const current = descendantQueue.shift();
        const children = fullGraph.outgoing.get(current) || [];
        children.forEach((child) => {
          if (!branch.has(child)) {
            branch.add(child);
            descendantQueue.push(child);
          }
        });
      }

      return branch;
    },
    [fullGraph.incoming, fullGraph.outgoing]
  );

  const selectedNodes = useMemo(() => {
    if (!selectedId) {
      return null;
    }
    return collectBranch(selectedId);
  }, [selectedId, collectBranch]);

  const hoverNodes = useMemo(() => {
    if (!hoveredId || selectedNodes) {
      return new Set();
    }
    return collectBranch(hoveredId);
  }, [hoveredId, selectedNodes, collectBranch]);

  const highlightedNodes = useMemo(() => {
    if (selectedNodes) {
      return selectedNodes;
    }
    return hoverNodes;
  }, [selectedNodes, hoverNodes]);

  const graph = useMemo(() => {
    if (!selectedNodes || selectedNodes.size === 0) {
      return fullGraph;
    }

    return buildCollapsedLayout(fullGraph, selectedNodes, selectedId);
  }, [fullGraph, selectedNodes, selectedId]);

  const beginPan = (event) => {
    if (event.button !== 0) {
      return;
    }

    event.preventDefault();

    setIsPanning(true);
    panOrigin.current = {
      x: event.clientX - viewportOffset.x,
      y: event.clientY - viewportOffset.y,
      pointerId: event.pointerId,
    };

    if (viewportRef.current) {
      viewportRef.current.setPointerCapture(event.pointerId);
    }
  };

  const updatePan = (event) => {
    if (!isPanning || !panOrigin.current) {
      return;
    }

    setViewportOffset({
      x: event.clientX - panOrigin.current.x,
      y: event.clientY - panOrigin.current.y,
    });
  };

  const endPan = () => {
    if (panOrigin.current && viewportRef.current) {
      viewportRef.current.releasePointerCapture(panOrigin.current.pointerId);
    }

    panOrigin.current = null;
    setIsPanning(false);
  };

  const clearSelection = useCallback(() => {
    setSelectedId(null);
    setHoveredId(null);
  }, []);

  const handleWheel = (event) => {
    event.preventDefault();
    const delta = event.deltaY;
    const zoomFactor = delta > 0 ? 0.92 : 1.08;
    setZoom((current) => {
      const next = Math.min(2.5, Math.max(0.4, current * zoomFactor));

      if (viewportRef.current) {
        const rect = viewportRef.current.getBoundingClientRect();
        const cx = event.clientX - rect.left;
        const cy = event.clientY - rect.top;

        const scaleChange = next / current;
        const newX = cx - scaleChange * (cx - viewportOffset.x);
        const newY = cy - scaleChange * (cy - viewportOffset.y);
        setViewportOffset({ x: newX, y: newY });
      }

      return next;
    });
  };

  useEffect(() => {
    if (!viewportRef.current) {
      return;
    }

    const viewportRect = viewportRef.current.getBoundingClientRect();

    if (selectedId && selectedNodes && selectedNodes.size > 0) {
      const contentWidth = graph.size.width;
      const contentHeight = graph.size.height;

      const targetZoom = 1;
      setZoom((prev) => (Math.abs(prev - targetZoom) < 0.001 ? prev : targetZoom));

      const offsetX = (viewportRect.width - contentWidth) / 2;
      const offsetY = (viewportRect.height - contentHeight) / 2;

      setViewportOffset((prev) => {
        if (Math.abs(prev.x - offsetX) < 0.5 && Math.abs(prev.y - offsetY) < 0.5) {
          return prev;
        }
        return {
          x: offsetX,
          y: offsetY,
        };
      });
    } else if (!selectedId) {
      setZoom((prev) => (Math.abs(prev - 1) < 0.001 ? prev : 1));
      setViewportOffset((prev) => {
        if (Math.abs(prev.x) < 0.5 && Math.abs(prev.y) < 0.5) {
          return prev;
        }
        return { x: 0, y: 0 };
      });
    }
  }, [selectedId, selectedNodes, graph.size.width, graph.size.height]);

  const resolveScore = useCallback(
    (nodeId) => {
      const entry = nodeScores.get(nodeId === "__organization_root" ? ROOT_NODE_ID : nodeId);
      if (!entry) {
        return null;
      }

      if (scoreContext.metricType === "overall") {
        return entry.overall;
      }

      if (scoreContext.metricType === "factor") {
        return entry.factors?.[scoreContext.metricId] ?? null;
      }

      if (scoreContext.metricType === "outcome") {
        return entry.outcomes?.[scoreContext.metricId] ?? null;
      }

      return null;
    },
    [nodeScores, scoreContext]
  );

  const baselineScore = resolveScore(ROOT_NODE_ID);
  const getEdgeStatus = useCallback(
    (sourceId, targetId) => {
      const targetScore = resolveScore(targetId);
      return getComparisonStatus(targetScore, baselineScore, targetId === ROOT_NODE_ID);
    },
    [baselineScore, resolveScore]
  );

  const getNodeStatus = useCallback(
    (nodeId) => {
      const scoreValue = resolveScore(nodeId);
      return getComparisonStatus(scoreValue, baselineScore, nodeId === ROOT_NODE_ID);
    },
    [baselineScore, resolveScore]
  );

  return (
    <Container>
      <Header>
        <HeaderRow>
          <div>
            <Title>Org Structure Analytics</Title>
            <Subtitle>
              Visualize cross-category relationships created by linking tag options.
            </Subtitle>
          </div>
          <BaselineBadge>
            <BaselineLabel>
              {typeof baselineScore === "number" && Number.isFinite(baselineScore)
                ? "Org Baseline"
                : "No Baseline"}
            </BaselineLabel>
            <BaselineScore>{formatScore(baselineScore)}</BaselineScore>
          </BaselineBadge>
        </HeaderRow>
        <ControlsRow>
          <ScoreSelect
            value={currentSelectValue}
            onChange={(event) => {
              const [metricType, metricId] = event.target.value.split("|");
              const next = scoreOptions.find((option) => option.value === event.target.value);
              if (next) {
                setScoreContext({
                  metricType,
                  metricId,
                  label: next.label,
                });
              }
            }}
          >
            {scoreOptions.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </ScoreSelect>
        </ControlsRow>
      </Header>

      {graph.nodes.length === 0 ? (
        <EmptyState>
          No linked category options were detected. As soon as options reference
          other categories, they will appear here.
        </EmptyState>
      ) : (
        <GraphViewport
          ref={viewportRef}
          onPointerDown={beginPan}
          onPointerMove={updatePan}
          onPointerUp={endPan}
          onPointerCancel={endPan}
          data-panning={isPanning}
          onWheel={handleWheel}
        >
          {selectedNodes && (
            <SelectionControls>
              <ClearSelectionButton
                onPointerDown={(event) => event.stopPropagation()}
                onClick={(event) => {
                  event.stopPropagation();
                  clearSelection();
                }}
              >
                Clear Selection
              </ClearSelectionButton>
            </SelectionControls>
          )}
          <GraphSurface
            style={{
              width: `${graph.size.width}px`,
              height: `${graph.size.height}px`,
              transform: `translate3d(${viewportOffset.x}px, ${viewportOffset.y}px, 0) scale(${zoom})`,
              transformOrigin: "0 0",
              transition: isPanning ? "none" : "transform 280ms cubic-bezier(0.22, 1, 0.36, 1)",
            }}
          >
            {selectedId === null ? (
              <GraphCanvas
                viewBox={`0 0 ${graph.size.width} ${graph.size.height}`}
                preserveAspectRatio="xMidYMid meet"
              >
                <defs>
                  <linearGradient id="edgeGradient" x1="0%" y1="0%" x2="100%" y2="0%">
                    <stop offset="0%" stopColor="#E2E8F6" />
                    <stop offset="100%" stopColor="#D8E1F0" />
                  </linearGradient>
                </defs>

                {graph.edges.map((edge) => {
                  const isActive = highlightedNodes.has(edge.source) && highlightedNodes.has(edge.target);
                  const sourceStatus = getNodeStatus(edge.source);
                  const targetStatus = getNodeStatus(edge.target);
                  const edgeStatus = targetStatus !== "neutral" ? targetStatus : sourceStatus;
                  const palette = edgeStatus === "positive"
                    ? { stroke: POSITIVE_EDGE_COLOR, inactive: POSITIVE_EDGE_COLOR_FADE }
                    : edgeStatus === "negative"
                      ? { stroke: NEGATIVE_EDGE_COLOR, inactive: NEGATIVE_EDGE_COLOR_FADE }
                      : { stroke: DEFAULT_EDGE_ACTIVE_COLOR, inactive: DEFAULT_EDGE_COLOR };

                  return (
                    <path
                      key={edge.id}
                      d={edge.path}
                      fill="none"
                      stroke={isActive ? palette.stroke : palette.inactive}
                      strokeWidth={isActive ? 2.4 : 1.2}
                      strokeOpacity={isActive ? 0.95 : (hoveredId && !isActive ? 0.28 : 0.6)}
                      style={{ transition: "stroke 220ms ease, stroke-width 220ms ease, stroke-opacity 320ms ease" }}
                    />
                  );
                })}
              </GraphCanvas>
            ) : null}

            {graph.nodes.map((node) => {
              const isHighlighted = highlightedNodes.has(node.id);
              const isDimmed = !!selectedNodes && !selectedNodes.has(node.id);
              const status = getNodeStatus(node.id);
              const scoreValue = resolveScore(node.id);

              return (
                <Node
                  key={node.id}
                  style={{ left: node.x - NODE_WIDTH / 2, top: node.y - NODE_HEIGHT / 2 }}
                  data-depth={node.depth}
                  data-priority={node.priority || ""}
                  $status={status}
                  onPointerDown={(event) => event.stopPropagation()}
                  onMouseEnter={() => {
                    if (!selectedNodes) {
                      setHoveredId(node.id);
                    }
                  }}
                  onMouseLeave={() => {
                    if (!selectedNodes) {
                      setHoveredId(null);
                    }
                  }}
                  onClick={(event) => {
                    event.stopPropagation();
                    setSelectedId((prev) => (prev === node.id ? null : node.id));
                  }}
                  $highlighted={isHighlighted}
                  $dimmed={isDimmed}
                >
                  <NodeAccent aria-hidden data-status={status} $dimmed={isDimmed} $highlighted={isHighlighted} />
                  <NodeContent>
                    <NodeBadge>{node.depth === 0 ? "Level 0" : `Level ${node.depth}`}</NodeBadge>
                    <NodeTitle>{node.name}</NodeTitle>
                    <NodeMeta>{node.categoryName}</NodeMeta>
                  </NodeContent>
                  <NodeScore data-status={status}>{formatScore(scoreValue)}</NodeScore>
                </Node>
              );
            })}
          </GraphSurface>

          <Legend>
            <LegendItem>
              <LegendSwatch />
              <LegendLabel>Hover to highlight connected options</LegendLabel>
            </LegendItem>
            <LegendItem>
              <LegendLevel>Level</LegendLevel>
              <LegendDescription>
                Click a node to isolate its lineage. Use Clear Selection to revert.
              </LegendDescription>
            </LegendItem>
          </Legend>
        </GraphViewport>
      )}
    </Container>
  );
};

export default Hierarchy;

const NODE_WIDTH = 220;
const NODE_HEIGHT = 96;

const buildGraphLayout = (categories, organizationName) => {
  if (!Array.isArray(categories) || categories.length === 0) {
    return emptyGraph(organizationName);
  }

  const optionMap = new Map();
  const rootNodeId = "__organization_root";

  categories.forEach((category) => {
    category?.options?.forEach((option) => {
      optionMap.set(option.id, {
        ...option,
        categoryId: category.id,
        categoryName: category.name,
        priority: option.priority || category.priority,
      });
    });
  });

  if (optionMap.size === 0) {
    return emptyGraph(organizationName);
  }

  const edges = [];
  const linkedIds = new Set();
  const parentsWithChildren = new Set();

  optionMap.forEach((option) => {
    if (!option.parent_id) {
      return;
    }

    const parentOption = optionMap.get(option.parent_id);

    if (!parentOption) {
      return;
    }

    edges.push({
      id: `${option.parent_id}-${option.id}`,
      source: option.parent_id,
      target: option.id,
    });

    linkedIds.add(option.id);
    linkedIds.add(option.parent_id);
    parentsWithChildren.add(option.parent_id);
  });

  if (edges.length === 0) {
    return emptyGraph(organizationName);
  }

  const topLevelParents = Array.from(parentsWithChildren).filter((parentId) => {
    const parentOption = optionMap.get(parentId);
    if (!parentOption) {
      return false;
    }

    const effectiveParentId = parentOption.parent_id;
    if (!effectiveParentId) {
      return true;
    }

    return !optionMap.has(effectiveParentId);
  });

  topLevelParents.forEach((parentId) => {
    edges.push({
      id: `${rootNodeId}-${parentId}`,
      source: rootNodeId,
      target: parentId,
    });

    linkedIds.add(parentId);
  });

  linkedIds.add(rootNodeId);

  const nodes = Array.from(linkedIds)
    .map((id) => {
      if (id === rootNodeId) {
        return {
          id: rootNodeId,
          name: organizationName,
          categoryName: "Organization",
          depth: 0,
          priority: "root",
        };
      }
      return optionMap.get(id);
    })
    .filter(Boolean);

  const depthMap = new Map();
  const visiting = new Set();

  const findDepth = (id) => {
    if (depthMap.has(id)) {
      return depthMap.get(id);
    }

    if (visiting.has(id)) {
      return 0;
    }

    visiting.add(id);

    if (id === rootNodeId) {
      depthMap.set(id, 0);
      visiting.delete(id);
      return 0;
    }

    const node = optionMap.get(id);
    if (!node) {
      depthMap.set(id, 0);
      visiting.delete(id);
      return 0;
    }

    const parentId = node.parent_id && optionMap.has(node.parent_id)
      ? node.parent_id
      : rootNodeId;

    const depth = findDepth(parentId) + 1;
    depthMap.set(id, depth);
    visiting.delete(id);
    return depth;
  };

  nodes.forEach((node) => {
    const depth = findDepth(node.id);
    node.depth = depth;
  });

  const levels = new Map();
  nodes.forEach((node) => {
    if (!levels.has(node.depth)) {
      levels.set(node.depth, []);
    }
    levels.get(node.depth).push(node);
  });

  const sortedDepths = Array.from(levels.keys()).sort((a, b) => a - b);
  const maxDepth = sortedDepths[sortedDepths.length - 1] || 0;

  const columnSpacing = 240;
  const horizontalMargin = 200;
  const baseVerticalSpacing = 130;

  let maxLevelCount = 0;
  sortedDepths.forEach((depth) => {
    const group = levels.get(depth) || [];
    maxLevelCount = Math.max(maxLevelCount, group.length);
  });

  const height = Math.max(560, (maxLevelCount + 1.5) * baseVerticalSpacing + 240);
  const width = Math.max(920, (maxDepth + 2) * columnSpacing + horizontalMargin * 2);

  sortedDepths.forEach((depth) => {
    const group = (levels.get(depth) || []).slice();
    group.sort((a, b) => {
      const nameCompare = a.categoryName.localeCompare(b.categoryName, undefined, { sensitivity: "base" });
      if (nameCompare !== 0) {
        return nameCompare;
      }
      return a.name.localeCompare(b.name, undefined, { sensitivity: "base" });
    });

    const spacing = height / (group.length + 1);

    group.forEach((node, index) => {
      const jitter = createJitter(node.id);
      const x = horizontalMargin + depth * columnSpacing;
      const y = spacing * (index + 1) + jitter;

      node.x = x;
      node.y = y;
    });
  });

  const adjacency = new Map();

  const edgesWithPath = edges
    .map((edge) => {
      const source = nodes.find((node) => node.id === edge.source);
      const target = nodes.find((node) => node.id === edge.target);

      if (!source?.x || !target?.x) {
        return null;
      }

      if (!adjacency.has(edge.source)) {
        adjacency.set(edge.source, new Set());
      }
      if (!adjacency.has(edge.target)) {
        adjacency.set(edge.target, new Set());
      }

      adjacency.get(edge.source).add(edge.target);
      adjacency.get(edge.target).add(edge.source);

      const controlOffset = Math.max(60, Math.abs(target.x - source.x) * 0.35);
      const c1x = source.x + controlOffset;
      const c1y = source.y;
      const c2x = target.x - controlOffset;
      const c2y = target.y;

      const path = `M ${source.x} ${source.y} C ${c1x} ${c1y} ${c2x} ${c2y} ${target.x} ${target.y}`;

      return {
        ...edge,
        path,
      };
    })
    .filter(Boolean);

  const incoming = new Map();
  const outgoing = new Map();

  edges.forEach((edge) => {
    if (!outgoing.has(edge.source)) {
      outgoing.set(edge.source, new Set());
    }
    if (!incoming.has(edge.target)) {
      incoming.set(edge.target, new Set());
    }

    outgoing.get(edge.source).add(edge.target);
    incoming.get(edge.target).add(edge.source);
  });

  return {
    nodes: nodes.map((node) => ({
      id: node.id,
      name: node.name,
      categoryName: node.categoryName,
      depth: node.depth,
      priority: node.priority,
      x: node.x,
      y: node.y,
    })),
    edges: edgesWithPath,
    adjacency: new Map(Array.from(outgoing.entries()).map(([key, value]) => [key, Array.from(value)])),
    incoming: new Map(Array.from(incoming.entries()).map(([key, value]) => [key, Array.from(value)])),
    outgoing: new Map(Array.from(outgoing.entries()).map(([key, value]) => [key, Array.from(value)])),
    size: { width, height },
  };
};

const emptyGraph = (organizationName = "Organization") => ({
  nodes: [
    {
      id: "__organization_root",
      name: organizationName,
      categoryName: "Organization",
      depth: 0,
      priority: "root",
      x: 400,
      y: 280,
    },
  ],
  edges: [],
  adjacency: new Map(),
  size: { width: 800, height: 560 },
});

const buildCollapsedLayout = (fullGraph, activeNodes, selectedId) => {
  const nodes = fullGraph.nodes.filter((node) => activeNodes.has(node.id));
  const edges = fullGraph.edges.filter(
    (edge) => activeNodes.has(edge.source) && activeNodes.has(edge.target)
  );

  if (nodes.length === 0) {
    return {
      nodes,
      edges: [],
      adjacency: new Map(),
      size: fullGraph.size,
    };
  }

  const rootId = "__organization_root";
  const columnSpacing = 240;
  const rowSpacing = 120;

  const graphNodes = [];
  const adjacency = new Map();

  const sortedNodes = nodes
    .slice()
    .sort((a, b) => a.depth - b.depth || a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));

  const levels = new Map();
  sortedNodes.forEach((node) => {
    if (!levels.has(node.depth)) {
      levels.set(node.depth, []);
    }
    levels.get(node.depth).push(node);
  });

  levels.forEach((group, depth) => {
    const sorted = group.slice().sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: "base" }));
    sorted.forEach((node, index) => {
      graphNodes.push({
        ...node,
        depth,
        columnIndex: depth,
        rowIndex: index,
      });
    });
  });

  const depthKeys = Array.from(levels.keys()).sort((a, b) => a - b);
  const maxDepth = depthKeys[depthKeys.length - 1] || 0;
  const depthCounts = depthKeys.map((depth) => levels.get(depth)?.length || 0);
  const maxRows = Math.max(...depthCounts, 1);

  const width = Math.max(600, (maxDepth + 1) * columnSpacing + NODE_WIDTH * 1.8);
  const height = Math.max(400, maxRows * rowSpacing + NODE_HEIGHT * 1.5);

  const depthHeights = depthKeys.reduce((acc, depth) => {
    const groupSize = levels.get(depth)?.length || 0;
    const totalHeight = (groupSize - 1) * rowSpacing;
    acc[depth] = {
      baseY: height / 2 - totalHeight / 2,
      size: groupSize,
    };
    return acc;
  }, {});

  graphNodes.forEach((node) => {
    const depthInfo = depthHeights[node.depth];
    if (!depthInfo) {
      return;
    }

    node.x = columnSpacing / 2 + node.depth * columnSpacing;
    node.y = depthInfo.baseY + node.rowIndex * rowSpacing;
  });

  const nodesById = new Map(graphNodes.map((node) => [node.id, node]));

  edges.forEach((edge) => {
    const source = nodesById.get(edge.source);
    const target = nodesById.get(edge.target);

    if (!source || !target) {
      return;
    }

    if (!adjacency.has(edge.source)) {
      adjacency.set(edge.source, new Set());
    }
    if (!adjacency.has(edge.target)) {
      adjacency.set(edge.target, new Set());
    }
    adjacency.get(edge.source).add(edge.target);
    adjacency.get(edge.target).add(edge.source);
  });

  const edgesWithPath = edges
    .map((edge) => {
      const source = nodesById.get(edge.source);
      const target = nodesById.get(edge.target);

      if (!source || !target) {
        return null;
      }

      const minX = Math.min(source.x, target.x);
      const maxX = Math.max(source.x, target.x);
      const spread = Math.max(60, (maxX - minX) * 0.35);

      const c1x = source.x + spread;
      const c1y = source.y;
      const c2x = target.x - spread;
      const c2y = target.y;

      return {
        ...edge,
        path: `M ${source.x} ${source.y} C ${c1x} ${c1y} ${c2x} ${c2y} ${target.x} ${target.y}`,
      };
    })
    .filter(Boolean);

  const adjacencyAsArrays = new Map();
  adjacency.forEach((value, key) => {
    adjacencyAsArrays.set(key, Array.from(value));
  });

  return {
    nodes: graphNodes,
    edges: edgesWithPath,
    adjacency: adjacencyAsArrays,
    size: { width, height },
  };
};

const createJitter = (id) => {
  if (!id) {
    return 0;
  }

  let hash = 0;
  for (let i = 0; i < id.length; i += 1) {
    hash = (hash << 5) - hash + id.charCodeAt(i);
    hash |= 0;
  }

  const normalized = (hash % 21) - 10; // range -10 to 10
  return normalized * 2;
};

const Container = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: calc(100vh - 90px);
  padding: 10px 0px; 
  box-sizing: border-box;
  font-family: "Raleway", sans-serif;
  overflow: hidden;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  margin-bottom: 24px;
`;

const Title = styled.h2`
  font-size: 24px;
  font-weight: 600;
  color: #2a3039;
  margin: 0;
`;

const Subtitle = styled.p`
  margin: 0;
  font-size: 14px;
  color: #5a6373;
  max-width: 720px;
  line-height: 1.6;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 24px;
`;

const BaselineBadge = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  padding: 10px 16px;
  border-radius: 12px;
  background: rgba(31, 39, 51, 0.04);
  border: 1px solid rgba(31, 39, 51, 0.08);
`;

const BaselineLabel = styled.span`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6c778a;
`;

const BaselineScore = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2733;
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
  padding: 0 0 12px;
`;

const GraphViewport = styled.div`
  position: relative;
  flex: 1;
  min-height: 0;
  padding: 0;
  overflow: hidden;
  box-sizing: border-box;
  cursor: ${(props) => (props["data-panning"] ? "grabbing" : "grab")};
  background: radial-gradient(circle at center, rgba(224, 233, 250, 0.36), rgba(255, 255, 255, 0));
`;

const GraphSurface = styled.div`
  position: relative;
  min-width: 100%;
  min-height: 100%;
`;

const GraphCanvas = styled.svg`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const Node = styled.div`
  position: absolute;
  width: ${NODE_WIDTH}px;
  height: ${NODE_HEIGHT}px;
  border-radius: 16px;
  background: ${(props) => (props.$highlighted ? "#ffffff" : "rgba(255, 255, 255, 0.9)")};
  border: 1px solid ${(props) => NODE_BORDER_COLORS[props.$status || "neutral"]};
  box-shadow: ${(props) =>
    props.$highlighted
      ? NODE_HIGHLIGHT_SHADOWS[props.$status || "neutral"]
      : props.$dimmed
        ? "0 6px 14px rgba(18, 24, 38, 0.05)"
        : "0 18px 38px rgba(17, 28, 55, 0.08)"};
  display: flex;
  align-items: center;
  padding: 14px 18px;
  box-sizing: border-box;
  cursor: pointer;
  opacity: ${(props) => (props.$dimmed ? 0.12 : 1)};
  transform: ${(props) => (props.$highlighted ? "translateY(-4px) scale(1.02)" : props.$dimmed ? "scale(0.98)" : "none")};
  transition: border 200ms ease, box-shadow 220ms ease, transform 220ms ease, opacity 240ms ease;
`;

const NodeAccent = styled.span`
  position: absolute;
  inset: 0;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(45, 112, 226, 0.1), rgba(120, 82, 240, 0.05));
  opacity: ${(props) => (props.$dimmed ? 0 : props.$highlighted ? 1 : 0)};
  transition: opacity 180ms ease, background 180ms ease;

  &[data-status="positive"] {
    background: linear-gradient(135deg, rgba(39, 205, 167, 0.18), rgba(39, 205, 167, 0.08));
  }

  &[data-status="negative"] {
    background: linear-gradient(135deg, rgba(241, 80, 98, 0.22), rgba(241, 80, 98, 0.1));
  }
`;

const NodeContent = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const NodeBadge = styled.span`
  align-self: flex-start;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #7c8aa8;
  background: rgba(125, 141, 190, 0.16);
  border-radius: 999px;
  padding: 4px 10px;
`;

const NodeTitle = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1f2733;
  letter-spacing: -0.01em;
`;

const NodeMeta = styled.span`
  font-size: 12px;
  color: #6c778a;
  letter-spacing: 0.01em;
`;

const NodeScore = styled.span`
  position: absolute;
  top: 16px;
  right: 18px;
  font-size: 13px;
  font-weight: 600;
  letter-spacing: 0.01em;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(31, 39, 51, 0.06);
  color: #1f2733;
  transition: background 180ms ease, color 180ms ease;

  &[data-status="positive"] {
    background: rgba(39, 205, 167, 0.18);
    color: #0f6c52;
  }

  &[data-status="negative"] {
    background: rgba(241, 80, 98, 0.18);
    color: #b22f3d;
  }
`;

const Legend = styled.div`
  position: absolute;
  bottom: 24px;
  right: 24px;
  display: flex;
  gap: 18px;
  align-items: center;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #dde3f2;
  border-radius: 14px;
  padding: 14px 18px;
  box-shadow: 0 20px 40px rgba(17, 28, 55, 0.12);
  backdrop-filter: blur(12px);
`;

const SelectionControls = styled.div`
  position: absolute;
  top: 24px;
  right: 24px;
  display: flex;
  gap: 10px;
  pointer-events: auto;
  z-index: 20;
`;

const ClearSelectionButton = styled.button`
  padding: 8px 14px;
  border-radius: 10px;
  border: none;
  background: #2d70e2;
  color: #fff;
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.02em;
  cursor: pointer;
  transition: background 200ms ease;

  &:hover {
    background: #1f5ed2;
  }
`;

const LegendItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 140px;
`;

const LegendSwatch = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  font-weight: 600;
  color: #2d70e2;

  &::before {
    content: "";
    width: 24px;
    height: 4px;
    border-radius: 999px;
    background: linear-gradient(90deg, #abc6ff, #2d70e2);
    box-shadow: 0 2px 6px rgba(45, 112, 226, 0.3);
  }
`;

const LegendLabel = styled.span`
  font-size: 12px;
  color: #5a6373;
`;

const LegendLevel = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: #2a3039;
`;

const LegendDescription = styled.span`
  font-size: 12px;
  color: #5a6373;
  line-height: 1.5;
`;

const EmptyState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 360px;
  border: 1px dashed #d6ddeb;
  border-radius: 14px;
  color: #7b8495;
  font-size: 14px;
  letter-spacing: 0.02em;
  text-align: center;
  padding: 24px;
`;

const ScoreSelect = styled.select`
  padding: 10px 14px;
  border-radius: 12px;
  border: 1px solid rgba(31, 39, 51, 0.16);
  background: #ffffff;
  font-size: 13px;
  color: #1f2733;
  font-family: "Raleway", sans-serif;
  cursor: pointer;
  min-width: 220px;
`;


